/**
 * UI copy — English / Urdu (اردو).
 * Layout stays LTR for both locales so nav, mega-menu, and flex grids stay aligned.
 */
export const dictionaries = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      solutions: 'Solutions',
      products: 'Products',
      portfolio: 'Portfolio',
      team: 'Team',
      company: 'Company',
      contact: 'Contact',
      bookConsultation: 'Book Free Consultation',
      viewServicesSecondary: 'Explore Services',
      companyTeamTitle: 'Team',
      companyTeamDesc: 'Leadership, engineers, and specialists behind every delivery.',
      companyBlogTitle: 'Blog',
      companyBlogDesc: 'Insights on AI, software delivery, and digital operations.',
      companyCasesTitle: 'Case studies',
      companyCasesDesc: 'Proof-led stories: problems, solutions, and outcomes.',
      companyAboutTitle: 'About',
      companyAboutDesc: 'Mission, registration, and how we work with clients.',
      companyExpertise: 'Expertise',
      companyExpertiseHint: 'Deep-dive solution pages for each capability.',
      langShort: 'اردو',
      langAria: 'Switch interface language to Urdu',
      mega: {
        development: 'Development',
        aiData: 'AI & data',
        growth: 'Growth',
        webTitle: 'Web development',
        webDesc: 'Conversion-ready platforms, portals, and internal systems.',
        mobileTitle: 'Mobile development',
        mobileDesc: 'iOS, Android, and cross‑platform product delivery.',
        aiTitle: 'AI solutions',
        aiDesc: 'Copilots, agents, LLM apps, and automation that ship.',
        dataTitle: 'Data engineering',
        dataDesc: 'Pipelines, warehouses, and governed analytics foundations.',
        mktTitle: 'Digital marketing',
        mktDesc: 'SEO, paid media, and attribution you can trust.',
        viewAll: 'View all services',
        ctaHint: 'Tell us your bottleneck — we map a plan in one call.',
      },
    },
    footer: {
      conversionTitle: 'Ready to automate your business?',
      conversionText:
        'Tell us what slows you down today — we will map a practical software or AI path you can ship.',
      trustLineSuffix:
        'Registered in Pakistan | AI, software & automation for local and international businesses.',
      brandTitle: 'MSA Technologies',
      brandBody:
        'We build AI automation, custom software, web and mobile products, and data systems so your teams spend less time on manual work and more time on growth.',
      quickLinks: 'Quick links',
      solutionsCol: 'Solutions',
      contactCol: 'Contact',
      remoteLine: 'Pakistan — remote & on-site engagements',
      caseStudies: 'Case studies',
      blog: 'Blog',
      companyCol: 'Company',
      servicesCol: 'Services & products',
      whatsappCta: 'Need a custom solution? WhatsApp us today.',
      registeredBadge: 'MSA Technologies — Registered in Pakistan',
      brandTagline: 'AI, software & automation solutions for serious operators.',
      leadTitle: 'Get the 2026 delivery playbook + a free consult slot',
      leadSubtitle:
        'Share your work email — we will send the playbook and you can book time with our team.',
      leadPlaceholder: 'Enter work email',
      leadSubmit: 'Send Playbook',
    },
    home: {
      trustTitle: 'Trusted by ambitious teams across industries',
      heroScroll: 'Scroll to explore',
      testimonialsTitle: 'What our clients say',
      testimonialsSubtitle: 'Real feedback from businesses we have transformed',
      ctaBottomTitle: 'Need a custom software or AI automation solution?',
      ctaBottomSubtitle:
        'Book a free consultation — we will clarify scope, risks, and a sensible first milestone before you commit.',
      slides: {
        s1: {
          title: 'AI Automation & Custom Software Solutions for Modern Businesses',
          subtitle:
            'MSA Technologies builds web applications, mobile apps, AI agents, automation systems, and custom business software for Pakistan and international markets.',
        },
        s2: {
          title: 'We solve business problems with intelligent systems',
          subtitle:
            'Clear milestones, production-minded engineering, and outcomes you can measure — from discovery to launch and beyond.',
        },
      },
      testimonials: {
        t1: {
          name: 'Sarah Johnson',
          role: 'CTO, TechCorp Solutions',
          content:
            'MSA Technologies transformed our entire digital infrastructure. Their AI solutions increased our efficiency by 300%.',
        },
        t2: {
          name: 'Ahmed Hassan',
          role: 'CEO, Digital Innovations',
          content:
            "The team's expertise in modern technologies and attention to detail is unmatched. Highly recommended!",
        },
        t3: {
          name: 'Maria Rodriguez',
          role: 'Director, Global Systems',
          content:
            'From concept to deployment, MSA Technologies delivered beyond our expectations. World-class service!',
        },
      },
    },
    portfolio: {
      heroTitle: 'Our portfolio',
      heroSubtitle:
        'Selected engagements with measurable outcomes — product screenshots and delivery snapshots.',
      featuredTitle: 'Featured projects',
      featuredSubtitle:
        'High‑fidelity UI, production systems, and client outcomes across sectors.',
      impactTitle: 'Project impact & results',
      clientsTitle: 'What our clients say',
    },
  },
  ur: {
    nav: {
      home: 'ہوم',
      services: 'سروسز',
      solutions: 'حل',
      products: 'مصنوعات',
      portfolio: 'پورٹ فولیو',
      team: 'ٹیم',
      company: 'کمپنی',
      contact: 'رابطہ',
      bookConsultation: 'مفت مشاورت بک کریں',
      viewServicesSecondary: 'سروسز دیکھیں',
      companyTeamTitle: 'ٹیم',
      companyTeamDesc: 'قیادت، انجینئرز، اور ہر ڈلیوری کے ماہرین۔',
      companyBlogTitle: 'بلاگ',
      companyBlogDesc: 'AI، سافٹ ویئر، اور ڈیجیٹل آپریشنز پر بصیرتیں۔',
      companyCasesTitle: 'کیس اسٹڈیز',
      companyCasesDesc: 'مسئلہ، حل، اور نتائج کی واضح کہانیاں۔',
      companyAboutTitle: 'ہمارے بارے میں',
      companyAboutDesc: 'مشن، رجسٹریشن، اور کلائنٹس کے ساتھ کام کا طریقہ۔',
      companyExpertise: 'مہارت',
      companyExpertiseHint: 'ہر صلاحیت کے لیے تفصیلی صفحات۔',
      langShort: 'EN',
      langAria: 'Switch interface language to English',
      mega: {
        development: 'ڈیولپمنٹ',
        aiData: 'AI اور ڈیٹا',
        growth: 'نشوونما',
        webTitle: 'ویب ڈیولپمنٹ',
        webDesc: 'تیز، قابلِ پیمائش ویب پلیٹ فارم، پورٹلز، اور اندرونی سسٹمز۔',
        mobileTitle: 'موبائل ڈیولپمنٹ',
        mobileDesc: 'iOS، اینڈرائیڈ، اور کراس پلیٹ فارم پر مصنوعات کی فراہمی۔',
        aiTitle: 'AI حل',
        aiDesc: 'کوپائلٹس، ایجنٹس، LLM ایپس، اور عملی آٹومیشن۔',
        dataTitle: 'ڈیٹا انجینئرنگ',
        dataDesc: 'پائپ لائنز، ڈیٹا وئیر ہاؤس، اور قابلِ اعتماد تجزیات۔',
        mktTitle: 'ڈیجیٹل مارکیٹنگ',
        mktDesc: 'SEO، اشتہارات، اور واضح attribution۔',
        viewAll: 'تمام سروسز دیکھیں',
        ctaHint: 'اپنا مسئلہ بتائیں — ایک کال میں اگلا قدم طے کریں۔',
      },
    },
    footer: {
      conversionTitle: 'اپنے کاروبار میں خودکار عمل درآمد کے لیے تیار ہیں؟',
      conversionText:
        'آج کیا آپ کو سست کر رہا ہے بتائیں — ہم عملی سافٹ ویئر یا AI کا راستہ واضح کریں گے۔',
      trustLineSuffix:
        'پاکستان میں رجسٹرڈ | FBR کے تقاضوں کے ساتھ | مقامی اور بین الاقوامی کاروبار کے لیے AI، سافٹ ویئر اور آٹومیشن۔',
      brandTitle: 'MSA Technologies',
      brandBody:
        'ہم AI آٹومیشن، کسٹم سافٹ ویئر، ویب اور موبائل مصنوعات، اور ڈیٹا سسٹمز بناتے ہیں تاکہ ٹیمیں دستی کام کم اور ترقی پر زیادہ وقت دیں۔',
      quickLinks: 'فوری روابط',
      solutionsCol: 'حل',
      contactCol: 'رابطہ',
      remoteLine: 'پاکستان — ریموٹ اور آن سائٹ تعاون',
      caseStudies: 'کیس اسٹڈیز',
      blog: 'بلاگ',
      companyCol: 'کمپنی',
      servicesCol: 'سروسز اور مصنوعات',
      whatsappCta: 'کسٹم حل چاہیے؟ آج ہی WhatsApp کریں۔',
      registeredBadge: 'MSA Technologies — پاکستان میں رجسٹرڈ',
      brandTagline: 'AI، سافٹ ویئر اور کاروبار کے لیے آٹومیشن۔',
      leadTitle: '2026 ڈلیوری پلے بک + مفت مشاورت',
      leadSubtitle:
        'اپنا آفیشل ای میل بھیجیں — ہم پلے بک شیئر کریں گے اور وقت بک کر سکتے ہیں۔',
      leadPlaceholder: 'کام کا ای میل',
      leadSubmit: 'پلے بک بھیجیں',
    },
    home: {
      trustTitle: 'مختلف شعبوں میں جرات مندانہ ٹیموں کا اعتماد',
      heroScroll: 'مزید دیکھنے کے لیے سکرول کریں',
      testimonialsTitle: 'ہمارے کلائنٹس کیا کہتے ہیں',
      testimonialsSubtitle: 'ان کاروبار کی رائے جنہیں ہم نے تبدیل کیا',
      ctaBottomTitle: 'کسٹم سافٹ ویئر یا AI آٹومیشن چاہیے؟',
      ctaBottomSubtitle:
        'مفت مشاورت بک کریں — ہم دائرہ کار، خطرات، اور پہلا معقول سنگ میل واضح کریں گے۔',
      slides: {
        s1: {
          title: 'جدید کاروبار کے لیے AI آٹومیشن اور کسٹم سافٹ ویئر',
          subtitle:
            'MSA Technologies ویب ایپلیکیشنز، موبائل ایپس، AI ایجنٹس، آٹومیشن سسٹمز، اور کسٹم بزنس سافٹ ویئر پاکستان اور بین الاقوامی منڈیوں کے لیے بناتا ہے۔',
        },
        s2: {
          title: 'ہم کاروباری مسائل کو ذہین سسٹمز سے حل کرتے ہیں',
          subtitle:
            'واضح سنگ میل، پروڈکشن کی سوچ، اور ناپنے والا اثر — دریافت سے لے کر لانچ اور اس کے بعد تک۔',
        },
      },
      testimonials: {
        t1: {
          name: 'Sarah Johnson',
          role: 'CTO، TechCorp Solutions',
          content:
            'MSA Technologies نے ہمارے ڈیجیٹل انفراسٹرکچر کو مکمل طور پر بہتر بنایا۔ ان کے AI حل نے کارکردگی میں نمایاں اضافہ کیا۔',
        },
        t2: {
          name: 'Ahmed Hassan',
          role: 'CEO، Digital Innovations',
          content:
            'جدید ٹیکنالوجی میں ان کی مہارت اور تفصیل پر توجہ بے مثال ہے۔ بھرپور سفارش!',
        },
        t3: {
          name: 'Maria Rodriguez',
          role: 'Director، Global Systems',
          content:
            'خیال سے لے کر تعیناتی تک MSA Technologies نے توقعات سے بڑھ کر کام کیا۔ عالمی معیار کی خدمت۔',
        },
      },
    },
    portfolio: {
      heroTitle: 'ہمارا پورٹ فولیو',
      heroSubtitle:
        'نمایاں منصوبے — UI کی واضح تصاویر اور نتائج پر مبنی کہانیاں۔',
      featuredTitle: 'نمایاں منصوبے',
      featuredSubtitle: 'مختلف شعبوں میں اثر اور تکنیکی گہرائی۔',
      impactTitle: 'اثر اور نتائج',
      clientsTitle: 'کلائنٹس کی رائے',
    },
  },
};

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'ur'];
